// Application entry point - v2
import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from './store/hooks';
import { migrateCompletedOrders } from './store/slices/ordersSlice';
import { fetchClients } from './store/slices/clientsSlice';
import { fetchSuppliers } from './store/slices/suppliersSlice';
import { fetchFillings, fetchComponents } from './store/slices/recipesSlice';
import { fetchBatchRecords } from './store/slices/batchRecordsSlice';
import { fetchRawIngredients } from './store/slices/rawIngredientsSlice';
import { fetchIngredientReceivingEntries } from './store/slices/ingredientReceivingSlice';
import { fetchIngredientTraceCodes } from './store/slices/ingredientTraceCodesSlice';
import { fetchDeliveryLogs } from './store/slices/deliveryLogsSlice';
import { fetchTraceCodes } from './store/slices/traceCodesSlice';
import { fetchBakingProductionLogs } from './store/slices/bakingProductionLogSlice';
import { emailScheduler } from './services/emailSchedulerService';
import { Home } from './pages/Home';
import { Login } from './pages/Login';
import { Register } from './pages/Register';
import { Inventory } from './pages/Inventory';
import { CompletedOrders } from './pages/CompletedOrders';
import { Insights } from './pages/Insights';
import { Expenses } from './pages/Expenses';
import { MassEmail } from './pages/MassEmail';
import { ContactParser } from './pages/ContactParser';
import { OrderParser } from './pages/OrderParser';
import { Suppliers } from './pages/Suppliers';
import { IngredientTracking } from './pages/IngredientTracking';
import { ProductTracking } from './pages/ProductTracking';
import { AdminReview } from './pages/AdminReview';
import { TemperatureRecords } from './pages/TemperatureRecords';
import { SanitationRecords } from './pages/SanitationRecords';
import { LciLunchOrders } from './pages/LciLunchOrders';
import EmailOrderProcessor from './pages/EmailOrderProcessor';
import { Pricing } from './pages/Pricing';
import { ManageTeam } from './pages/ManageTeam';
import { Profile } from './pages/Profile';
import { Workflow } from './pages/Workflow';
import { Fillings } from './features/recipes/Fillings';
import { Components } from './features/recipes/Components';
import { Orders } from './features/orders/Orders';
import { Clients } from './features/clients/Clients';
import { ClientDetailPage } from './features/clients/ClientDetailPage';
import Calendar from './features/calendar/Calendar';
import { DebugPanel } from './components/DebugPanel';
import { Sidebar } from './components/Sidebar';
import { ErrorBoundary } from './components/ErrorBoundary';
import { ToastContainer } from './components/ToastContainer';
import { AuthInitializer } from './components/AuthInitializer';
import './App.css';

// Import diagnostics tool for development (makes runTaskColorDiagnostics() available in console)
if (process.env.NODE_ENV === 'development') {
  import('./utils/taskColorDiagnostics');
}

function AppContent() {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const orders = useAppSelector(state => state.orders.orders);
  const clients = useAppSelector(state => state.clients.clients);
  const fillings = useAppSelector(state => state.recipes.fillings);
  const { isAuthenticated, isCheckingSession, user } = useAppSelector(state => state.auth);

  // Redirect to login if not authenticated (unless already on login or register page)
  useEffect(() => {
    if (!isCheckingSession && !isAuthenticated &&
        location.pathname !== '/login' &&
        !location.pathname.startsWith('/register/')) {
      navigate('/login', { replace: true, state: { from: location } });
    }

    // Team users can only access /workflow - redirect them if they try other routes
    if (!isCheckingSession && isAuthenticated && user?.role === 'team') {
      const allowedPaths = ['/workflow', '/login', '/admin/profile'];
      const isAllowed = allowedPaths.some(path =>
        location.pathname === path || location.pathname.startsWith('/register/')
      );
      if (!isAllowed) {
        navigate('/workflow', { replace: true });
      }
    }
  }, [isAuthenticated, isCheckingSession, location, navigate, user]);

  // Run migration on app initialization to move any completed orders to completedOrders array
  useEffect(() => {
    dispatch(migrateCompletedOrders());
  }, [dispatch]);

  // Fetch all data from API on app initialization
  useEffect(() => {
    dispatch(fetchClients());
    dispatch(fetchSuppliers());
    dispatch(fetchFillings());
    dispatch(fetchComponents());
    dispatch(fetchBatchRecords());
    dispatch(fetchRawIngredients());
    dispatch(fetchIngredientReceivingEntries());
    dispatch(fetchIngredientTraceCodes());
    dispatch(fetchDeliveryLogs());
    dispatch(fetchTraceCodes());
    dispatch(fetchBakingProductionLogs());
  }, [dispatch]);

  // Initialize email scheduler
  useEffect(() => {
    emailScheduler.initialize(dispatch, () => ({
      orders,
      clients,
      recipes: fillings.map(f => ({ id: f.id, name: f.name }))
    }));

    return () => {
      emailScheduler.stop();
    };
  }, [dispatch, orders, clients, fillings]);

  const isPublicPage = location.pathname === '/login' || location.pathname.startsWith('/register/');
  const isTeamUser = isAuthenticated && user?.role === 'team';

  return (
    <div className="App">
      {!isPublicPage && !isTeamUser && (
        <ErrorBoundary sectionName="Sidebar">
          <Sidebar />
        </ErrorBoundary>
      )}
      <main className="main-content" style={(isPublicPage || isTeamUser) ? { marginLeft: 0 } : {}}>
        <Routes>
          <Route
            path="/login"
            element={
              <ErrorBoundary sectionName="Login">
                <Login />
              </ErrorBoundary>
            }
          />
          <Route
            path="/register/:token"
            element={
              <ErrorBoundary sectionName="Register">
                <Register />
              </ErrorBoundary>
            }
          />
          <Route
            path="/"
            element={
              <ErrorBoundary sectionName="Home">
                <Home />
              </ErrorBoundary>
            }
          />
          <Route
            path="/workflow"
            element={
              <ErrorBoundary sectionName="Workflow">
                <Workflow />
              </ErrorBoundary>
            }
          />
          <Route
            path="/fillings"
            element={
              <ErrorBoundary sectionName="Fillings">
                <Fillings />
              </ErrorBoundary>
            }
          />
          <Route
            path="/components"
            element={
              <ErrorBoundary sectionName="Components">
                <Components />
              </ErrorBoundary>
            }
          />
          <Route
            path="/orders"
            element={
              <ErrorBoundary sectionName="Orders">
                <Orders />
              </ErrorBoundary>
            }
          />
          <Route
            path="/clients"
            element={
              <ErrorBoundary sectionName="Clients">
                <Clients />
              </ErrorBoundary>
            }
          />
          <Route
            path="/clients/:clientId"
            element={
              <ErrorBoundary sectionName="Client Detail">
                <ClientDetailPage />
              </ErrorBoundary>
            }
          />
          <Route
            path="/schedule"
            element={
              <ErrorBoundary sectionName="Calendar">
                <Calendar />
              </ErrorBoundary>
            }
          />
          <Route
            path="/inventory"
            element={
              <ErrorBoundary sectionName="Inventory">
                <Inventory />
              </ErrorBoundary>
            }
          />
          <Route
            path="/pricing"
            element={
              <ErrorBoundary sectionName="Pricing">
                <Pricing />
              </ErrorBoundary>
            }
          />
          <Route
            path="/completed-orders"
            element={
              <ErrorBoundary sectionName="Completed Orders">
                <CompletedOrders />
              </ErrorBoundary>
            }
          />
          <Route
            path="/insights"
            element={
              <ErrorBoundary sectionName="Insights">
                <Insights />
              </ErrorBoundary>
            }
          />
          <Route
            path="/expenses"
            element={
              <ErrorBoundary sectionName="Expenses">
                <Expenses />
              </ErrorBoundary>
            }
          />
          <Route
            path="/marketing/mass-email"
            element={
              <ErrorBoundary sectionName="Mass Email">
                <MassEmail />
              </ErrorBoundary>
            }
          />
          <Route
            path="/marketing/contact-parser"
            element={
              <ErrorBoundary sectionName="Contact Parser">
                <ContactParser />
              </ErrorBoundary>
            }
          />
          <Route
            path="/marketing/order-parser"
            element={
              <ErrorBoundary sectionName="Order Parser">
                <OrderParser />
              </ErrorBoundary>
            }
          />
          <Route
            path="/suppliers"
            element={
              <ErrorBoundary sectionName="Suppliers">
                <Suppliers />
              </ErrorBoundary>
            }
          />
          <Route
            path="/haccp/ingredient-tracking"
            element={
              <ErrorBoundary sectionName="Ingredient Tracking">
                <IngredientTracking />
              </ErrorBoundary>
            }
          />
          <Route
            path="/haccp/product-tracking"
            element={
              <ErrorBoundary sectionName="Product Tracking">
                <ProductTracking />
              </ErrorBoundary>
            }
          />
          <Route
            path="/haccp/temperature-records"
            element={
              <ErrorBoundary sectionName="Temperature Records">
                <TemperatureRecords />
              </ErrorBoundary>
            }
          />
          <Route
            path="/haccp/sanitation-records"
            element={
              <ErrorBoundary sectionName="Sanitation Records">
                <SanitationRecords />
              </ErrorBoundary>
            }
          />
          <Route
            path="/haccp/admin-review"
            element={
              <ErrorBoundary sectionName="Admin Review">
                <AdminReview />
              </ErrorBoundary>
            }
          />
          <Route
            path="/lci-lunch-orders"
            element={
              <ErrorBoundary sectionName="LCI Lunch Orders">
                <LciLunchOrders />
              </ErrorBoundary>
            }
          />
          <Route
            path="/email-order-processor"
            element={
              <ErrorBoundary sectionName="Email Order Processor">
                <EmailOrderProcessor />
              </ErrorBoundary>
            }
          />
          <Route
            path="/admin/manage-team"
            element={
              <ErrorBoundary sectionName="Manage Team">
                <ManageTeam />
              </ErrorBoundary>
            }
          />
          <Route
            path="/admin/profile"
            element={
              <ErrorBoundary sectionName="Profile">
                <Profile />
              </ErrorBoundary>
            }
          />
        </Routes>
      </main>
      <ErrorBoundary sectionName="Debug Panel">
        <DebugPanel />
      </ErrorBoundary>
      <ToastContainer />
    </div>
  );
}

function App() {
  return (
    <Router>
      <AuthInitializer>
        <AppContent />
      </AuthInitializer>
    </Router>
  );
}

export default App;
